import { HttpRequestError } from 'viem'
import { z } from 'zod'
import { createRequestScheduler } from '../requestScheduler'
import type { HeadersInput } from './types'

/** Explorer が HTTP 200 で返す制限エラーの判定用スキーマ。 */
const explorerErrorSchema = z.object({
  status: z.literal('0'),
  result: z.string(),
})

/** 待機可能な再試行間隔の上限。長い制限は後続の同期まで送信を保留する。 */
const maxRetryDelayMs = 30_000

/**
 * Retry-After を秒数または HTTP 日付として解釈する。
 * @param params レスポンスのヘッダー値と現在時刻。
 * @returns 有効な指定なら待機ミリ秒、指定がなければ undefined。
 */
export const getRetryAfterMs = ({
  value,
  now,
}: { value: string | null; now: number }): number | undefined => {
  if (value === null || value.trim() === '') return undefined
  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000
  const date = Date.parse(value)
  return Number.isFinite(date) ? Math.max(0, date - now) : undefined
}

/**
 * Explorer の HTTP 送信と有限回の再試行を管理する。
 * @param params 時計、待機関数、HTTP クライアント。テストでは外部通信せず差し替えられる。
 * @returns 接続先別の送信頻度・制限解除時刻を共有する JSON 取得関数。
 */
export const createExplorerRequester = ({
  now = Date.now,
  sleepMs = async ({ ms }: { ms: number }) => {
    await new Promise((resolve) => setTimeout(resolve, ms))
  },
  fetchImplementation = ({ url, init }: { url: string; init: RequestInit }) =>
    fetch(url, init),
}: {
  now?: () => number
  sleepMs?: (params: { ms: number }) => Promise<void>
  fetchImplementation?: (params: {
    url: string
    init: RequestInit
  }) => Promise<Response>
} = {}) => {
  const states = new Map<
    string,
    { retryAt: number; wait: ReturnType<typeof createRequestScheduler> }
  >()
  const groups = new Map<string, ReturnType<typeof createRequestScheduler>>()
  return async ({
    url,
    key,
    intervalMs,
    headers,
    rateGroup,
    body: requestBody,
    responseFormat = 'json',
  }: {
    url: string
    key: string
    intervalMs: number
    headers?: HeadersInput
    rateGroup?: string
    body?: string
    responseFormat?: 'json' | 'jsonl'
  }): Promise<unknown> => {
    let state = states.get(key)
    if (state === undefined) {
      const created: {
        retryAt: number
        wait: ReturnType<typeof createRequestScheduler>
      } = {
        retryAt: 0,
        wait: createRequestScheduler({
          intervalMs,
          now,
          sleepMs,
          getNotBefore: () => created.retryAt,
        }),
      }
      states.set(key, created)
      state = created
    }
    const deadline = now() + 60_000
    if (rateGroup !== undefined && !groups.has(rateGroup)) {
      groups.set(
        rateGroup,
        createRequestScheduler({ intervalMs: 500, now, sleepMs }),
      )
    }
    for (let attempt = 0; ; attempt += 1) {
      while (true) {
        await state.wait({ deadline })
        if (rateGroup !== undefined) await groups.get(rateGroup)?.({ deadline })
        // グループ待機中に先行応答が追加した制限も、送信前に確認する。
        if (state.retryAt <= now()) break
      }
      // 送信待ちの間に認証トークンが失効しないよう、headers は送信直前に毎回解決する。
      const resolvedHeaders =
        typeof headers === 'function' ? await headers() : headers
      let response: Response
      try {
        response = await fetchImplementation({
          url,
          init: {
            headers: resolvedHeaders,
            method: requestBody === undefined ? 'GET' : 'POST',
            body: requestBody,
            signal: AbortSignal.timeout(Math.min(10_000, deadline - now())),
          },
        })
      } catch (error) {
        if (
          attempt >= 3 ||
          !(
            error instanceof TypeError ||
            (error instanceof Error &&
              ['TimeoutError', 'AbortError'].includes(error.name))
          )
        )
          throw error
        await sleepMs({ ms: 1000 * 2 ** attempt })
        continue
      }
      const body: unknown = response.ok
        ? responseFormat === 'jsonl'
          ? (await response.text())
              .split('\n')
              .filter((line) => line.trim() !== '')
              .map((line): unknown => JSON.parse(line))
          : await response.json()
        : undefined
      const apiError = explorerErrorSchema.safeParse(body)
      const rateLimited =
        [429, 529].includes(response.status) ||
        (apiError.success &&
          /max (?:calls per sec )?rate limit reached/i.test(
            apiError.data.result,
          ))
      const transient =
        rateLimited ||
        [502, 503, 504].includes(response.status) ||
        (apiError.success &&
          apiError.data.result.includes('timeout or server too busy'))
      if (!response.ok || transient) {
        const error = new HttpRequestError({
          url,
          status: rateLimited ? 429 : response.status,
          details: apiError.success
            ? apiError.data.result
            : response.statusText,
        })
        // エラー応答の本文を破棄し、再試行中に接続を保持しない。
        if (!response.ok) await response.body?.cancel()
        const delay = Math.max(
          (rateLimited ? 5000 : 1000) * 2 ** attempt,
          getRetryAfterMs({
            value: response.headers.get('Retry-After'),
            now: now(),
          }) ?? 0,
        )
        if (rateLimited) state.retryAt = Math.max(state.retryAt, now() + delay)
        if (
          !transient ||
          attempt >= 3 ||
          delay > maxRetryDelayMs ||
          now() + delay >= deadline
        )
          throw error
        if (!rateLimited) await sleepMs({ ms: delay })
        continue
      }
      return body
    }
  }
}

/** アプリ全体の Explorer 送信枠とクールダウンを共有する取得関数。 */
export const requestExplorerJson = createExplorerRequester()
